import {
  Building2,
  Clock,
  CreditCard,
  FileText,
  Headphones,
  LifeBuoy,
  Image,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  ScrollText,
  Smartphone,
  Timer,
  MessageSquare,
  Receipt,
  Tag,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router';

import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/utils/cn';
import { useAuthStore } from '@/stores/auth-store';

const NAV_ITEMS = [
  { to: '/', labelKey: 'nav.overview', icon: LayoutDashboard, end: true },
  { to: '/churches', labelKey: 'nav.churches', icon: Building2 },
  { to: '/users', labelKey: 'nav.users', icon: Users },
  { to: '/audit', labelKey: 'nav.audit', icon: ScrollText },
  { to: '/support/tickets', labelKey: 'nav.supportTickets', icon: LifeBuoy },
  { to: '/support-access', labelKey: 'nav.supportAccess', icon: Headphones },
  { to: '/coupons', labelKey: 'nav.coupons', icon: Tag, adminOnly: true },
  { to: '/invoices', labelKey: 'nav.invoices', icon: FileText },
  { to: '/receipts', labelKey: 'nav.receipts', icon: Receipt },
  { to: '/email', labelKey: 'nav.email', icon: Mail },
  { to: '/sms', labelKey: 'nav.sms', icon: MessageSquare },
  { to: '/giving-payments', labelKey: 'nav.givingPayments', icon: CreditCard },
  { to: '/default-images', labelKey: 'nav.defaultImages', icon: Image },
  { to: '/app-version', labelKey: 'nav.appVersion', icon: Smartphone },
  { to: '/console-session', labelKey: 'nav.consoleSession', icon: Timer },
  { to: '/operations', labelKey: 'nav.operations', icon: Clock },
] as const;

const ShellLink = ({
  to,
  end,
  children,
  onNavigate,
}: {
  to: string;
  end?: boolean;
  children: ReactNode;
  onNavigate?: () => void;
}) => (
  <NavLink
    to={to}
    end={end}
    onClick={onNavigate}
    className={({ isActive }) =>
      cn(
        'flex items-center gap-2.5 rounded-md px-3 py-2 text-body-sm transition-colors',
        isActive
          ? 'bg-brand/10 font-medium text-brand'
          : 'text-fg-secondary hover:bg-surface-sunken hover:text-fg',
      )
    }
  >
    {children}
  </NavLink>
);

const SidebarPanel = ({
  onNavigate,
  showCloseButton,
  onClose,
}: {
  onNavigate?: () => void;
  showCloseButton?: boolean;
  onClose?: () => void;
}) => {
  const { t } = useTranslation(['admin', 'common']);
  const user = useAuthStore((state) => state.user);
  const navItems = NAV_ITEMS.filter(
    (item) => !('adminOnly' in item && item.adminOnly) || user?.platformRole === 'platform_admin',
  );

  return (
    <>
      <div className="flex min-h-14 shrink-0 items-center gap-2 border-b border-border px-4 py-2">
        <Building2 className="size-5 shrink-0 text-brand" aria-hidden />
        <p className="min-w-0 flex-1 text-body font-medium leading-snug">
          {t('app.name', { ns: 'common' })}
          <span className="mt-0.5 block text-caption font-normal text-fg-secondary">
            {t('nav.admin')}
          </span>
        </p>
        {showCloseButton ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0"
            aria-label={t('nav.closeMenu', { ns: 'common' })}
            onClick={onClose}
          >
            <X className="size-5" aria-hidden />
          </Button>
        ) : null}
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <nav className="flex flex-col gap-1 p-3" aria-label={t('nav.admin')}>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <ShellLink
                key={item.to}
                to={item.to}
                end={'end' in item ? item.end : undefined}
                onNavigate={onNavigate}
              >
                <Icon className="size-4 shrink-0" aria-hidden />
                {t(item.labelKey)}
              </ShellLink>
            );
          })}
        </nav>
      </ScrollArea>
    </>
  );
};

export const AdminShell = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation('admin');
  const location = useLocation();
  const signOut = useAuthStore((state) => state.signOut);
  const user = useAuthStore((state) => state.user);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen]);

  const closeMobileNav = () => setMobileNavOpen(false);

  return (
    <div className="flex h-dvh overflow-hidden bg-background text-fg">
      <aside className="hidden h-full w-60 shrink-0 flex-col border-r border-border bg-surface lg:flex">
        <SidebarPanel />
      </aside>

      {mobileNavOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-overlay backdrop-blur-[2px]"
            aria-label={t('nav.closeMenu', { ns: 'common', defaultValue: 'Close menu' })}
            onClick={closeMobileNav}
          />
          <aside className="relative flex h-full w-60 flex-col border-r border-border bg-surface shadow-e3">
            <SidebarPanel showCloseButton onClose={closeMobileNav} onNavigate={closeMobileNav} />
          </aside>
        </div>
      ) : null}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-border bg-surface px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 lg:hidden"
              aria-label={t('nav.openMenu', { ns: 'common', defaultValue: 'Open menu' })}
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu className="size-5" aria-hidden />
            </Button>
            <Link to="/" className="truncate text-body-sm font-medium text-fg lg:hidden">
              {t('nav.admin')}
            </Link>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {user ? (
              <span className="hidden text-body-sm text-fg-secondary sm:inline">
                {user.firstName} {user.lastName}
              </span>
            ) : null}
            <Button type="button" variant="outline" size="sm" onClick={() => void signOut()}>
              <LogOut className="size-4" aria-hidden />
              {t('nav.signOut')}
            </Button>
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[var(--content-max-width)] px-4 py-6 sm:px-6">{children}</div>
        </main>
      </div>
    </div>
  );
};
